import { useCallback, useEffect, useState } from 'react';
import bugReportService, { SubmitBugReportPayload } from '../api/services/bugReportService';
import { ApiUrls } from '../api/ApiUrls';
import { useTrackEvent } from '../analytics/hooks/useTrackEvent';

export function useReportProblem(options: { navigate: (to: string | number | string) => void; showToast: (opts: { message?: string; severity: 'success' | 'error' | 'info' | 'warning' }) => void; getConversationId?: () => string | null }) {
  const [issueType, setIssueType] = useState<string | null>(null);
  const [description, setDescription] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSuccess, setIsSuccess] = useState(false);

  const { trackEvent } = useTrackEvent();

  // Read stored source URL (saved when user clicked menu item)
  const getSourceUrl = useCallback(() => {
    try {
      return sessionStorage.getItem('ideoz_report_source_url');
    } catch (e) {
      return null;
    }
  }, []);

  useEffect(() => {
    trackEvent('ui.report_problem.opened');
  }, [trackEvent]);

  // Clear validation error when the user selects an issue type
  useEffect(() => {
    if (issueType && error) {
      setError(null);
    }
  }, [issueType, error]);

  const validate = useCallback(() => {
    if (!issueType) {
      setError('Please select an issue type');
      return false;
    }
    setError(null);
    return true;
  }, [issueType]);

  const submit = useCallback(async () => {
    if (!validate()) return;
    setIsSubmitting(true);
    // Resolve a sensible pageUrl for the bug report. Prefer the stored source URL,
    // then `document.referrer` (useful inside embedded/plugin contexts), then
    // `window.location.href`. As a last resort leave null and let the server
    // validate; but prefer a platform link if available.
    let pageUrl: string | null = null;
    try {
      const stored = getSourceUrl();
      if (stored) pageUrl = stored;
      else if (typeof document !== 'undefined' && document.referrer) pageUrl = document.referrer;
      else if (typeof window !== 'undefined' && window.location && window.location.href) pageUrl = window.location.href;
      if (pageUrl === '') pageUrl = null;

      // Ensure pageUrl is an absolute http(s) URL; otherwise fall back to platform link
      if (pageUrl) {
        try {
          const resolved = new URL(pageUrl, typeof window !== 'undefined' ? window.location.href : undefined as any);
          if (!['http:', 'https:'].includes(resolved.protocol)) {
            console.warn('useReportProblem: pageUrl has non-http protocol, falling back to platformLink', pageUrl);
            pageUrl = ApiUrls.platformLink || null;
          } else {
            pageUrl = resolved.href;
          }
        } catch (e) {
          console.warn('useReportProblem: invalid pageUrl, falling back to platformLink', pageUrl);
          pageUrl = ApiUrls.platformLink || null;
        }
      } else {
        pageUrl = ApiUrls.platformLink || null;
      }
    } catch (e) {
      pageUrl = ApiUrls.platformLink || null;
    }

    const payload: SubmitBugReportPayload = {
      issueType: issueType as any,
      description: description || null,
      conversationId: typeof options.getConversationId === 'function' ? options.getConversationId() : null,
      pageUrl,
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
    };

    try {
      await bugReportService.submitBugReport(payload);
      trackEvent('ui.report_problem.submitted', { issueType });
      // Show success dialog (page handles navigation on dismiss)
      setIsSuccess(true);
    } catch (e) {
      console.error('Failed to submit bug report', e);
      options.showToast({ message: 'Something went wrong. Please try again.', severity: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  }, [validate, issueType, description, getSourceUrl, options, trackEvent]);

  const cancel = useCallback(() => {
    const src = getSourceUrl();
    try { sessionStorage.removeItem('ideoz_report_source_url'); } catch (e) {}
    if (!src) {
      options.navigate(-1 as any);
      return;
    }

    try {
      if (typeof window !== 'undefined') {
        const url = new URL(src, window.location.href);
        if (url.origin === window.location.origin) {
          const path = url.pathname + url.search + url.hash;
          options.navigate(path as any);
        } else {
          // external origin — perform full navigation
          window.location.href = src;
        }
      } else {
        options.navigate(src as any);
      }
    } catch (e) {
      options.navigate(src as any);
    }
  }, [getSourceUrl, options]);

  const dismissSuccess = useCallback(() => {
    const src = getSourceUrl();
    try { sessionStorage.removeItem('ideoz_report_source_url'); } catch (e) {}
    setIsSuccess(false);

    if (!src) {
      options.navigate(-1 as any);
      return;
    }

    try {
      if (typeof window !== 'undefined') {
        const url = new URL(src, window.location.href);
        if (url.origin === window.location.origin) {
          const path = url.pathname + url.search + url.hash;
          options.navigate(path as any);
        } else {
          window.location.href = src;
        }
      } else {
        options.navigate(src as any);
      }
    } catch (e) {
      options.navigate(src as any);
    }
  }, [getSourceUrl, options]);

  return {
    issueType,
    setIssueType,
    description,
    setDescription,
    isSubmitting,
    error,
    submit,
    cancel,
    isSuccess,
    dismissSuccess,
  };
}

export default useReportProblem;
